import uuid from 'react-uuid';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { useEffect } from 'react';
import ReactGA from 'react-ga4';
import { includes, max, omit } from 'lodash';
import { getOrderPage, getOrderPages } from '../../common/AccountUtils';
import { OrderPageIntent } from '../OrderPageIntent';
import OrderUtils, { calculatePrice, SpecificEventError, VoucherError } from '../OrderUtils';
import {
  AccountData,
  AccountService,
  EventService,
  OrderPageData,
  SlimEventData,
  VoucherOut,
  VoucherService,
  VoucherStatus,
} from '../../api';
import { useLanguage } from '../../common/GeneralUtils';
import { OrderPageState } from './OrderPageState';
import { setUTMData } from '../orderCompletion/utmLocalStorage';
import { getOrderPageStateFromLocalStorage } from './orderPageStateLocalStorage';

type initOrderPageStateProps = {
  account: AccountData;
  voucher?: VoucherOut | null;
  voucherError?: VoucherError;
  specificEvent?: SlimEventData | null;
  specificEventError?: SpecificEventError;
};

type useInitOrderPageStateProps = {
  orderPageState: OrderPageState;
  setOrderPageState: (orderPageState: OrderPageState) => void;
};

const calculateInitialPersons = (
  orderPage: OrderPageData,
  sliderMinValue: number,
  voucher?: VoucherOut | null,
  preFilledPersons: string | undefined = undefined,
): number => {
  if (voucher?.persons) {
    return voucher.persons;
  }

  const hasMultipleTickets = orderPage.objects.some((obj: any) => obj.type === 'multiple_tickets_price');
  if (hasMultipleTickets) {
    return 0;
  }
  if (preFilledPersons) {
    const persons = parseInt(preFilledPersons, 10);
    if (persons) {
      return persons;
    }
  }
  return max([orderPage.persons, sliderMinValue]) || 0;
};

export const useInitOrderPageState = ({ orderPageState, setOrderPageState }: useInitOrderPageStateProps) => {
  const { accountId, orderPageId } = useParams();
  const language = useLanguage();
  const { i18n } = useTranslation();
  const posthog = usePostHog();
  const searchParams = new URLSearchParams(window.location.search);
  const onlyVouchers = searchParams.get('vouchers') === 'true';
  const onlyCouples = searchParams.get('couples') === 'true';
  const noMinDate = searchParams.get('nomindate') === 'true';
  const troubleshoot = searchParams.get('troubleshoot') === 'true';
  const preSelectedDate = searchParams.get('date') || undefined;
  const preFilledPersons = searchParams.get('persons') || undefined;
  const pTime = searchParams.get('time');
  const preSelectedTime = pTime ? parseInt(pTime, 10) : undefined;
  const voucherCode = searchParams.get('vouchercode');
  const specificEventId = searchParams.get('event');
  const initOrderPageState = ({
    account,
    voucher,
    voucherError,
    specificEvent,
    specificEventError,
  }: initOrderPageStateProps) => {
    const accountDefaultLanguage = account.default_system_language;
    if (accountDefaultLanguage !== language && !searchParams.get('language')) {
      void i18n.changeLanguage(accountDefaultLanguage);
    }
    const experience = account.experiences[0];

    let orderPage = getOrderPages(account)[0];
    if (orderPageId) {
      orderPage = getOrderPage(account, orderPageId);
    }

    orderPageState.orderPageIntent = OrderPageIntent.BUY_NOW;

    if (voucher) {
      orderPageState.orderPageIntent = OrderPageIntent.REDEEM_GIFT_CARD;
      orderPageState.isLoadingDates = true;
    }

    // @ts-ignore
    const sliderMinValue = orderPage.objects.find((x: any) => x.type === 'slider')?.min_value || 0;

    const tempDetails = {
      ...orderPageState.details,
      persons: calculateInitialPersons(orderPage, sliderMinValue, voucher, preFilledPersons),
      duration: voucher?.duration || orderPage.duration,
      custom_details: voucher?.custom_details || OrderUtils.getDefaultCustomDetails(orderPage),
    };

    posthog.setPersonPropertiesForFlags({ accountId: account.id });
    posthog.identify(uuid(), {
      accountId: account.id,
      orderPageId: orderPage.id,
      environment: import.meta.env.VITE_REACT_APP_ENVIRONMENT || 'local',
      context: 'order-page',
    });
    if (troubleshoot) {
      posthog.capture('Troubleshoot started', { orderPageId: orderPage.id });
    }
    const specificEventDateAndTimeDetails = specificEvent
      ? {
          dateAndTimeDetails: {
            date: specificEvent?.date,
            time: specificEvent?.time,
          },
        }
      : {};
    const specificPreselectedDateAndTimeDetails = preSelectedDate
      ? {
          dateAndTimeDetails: {
            date: preSelectedDate,
            time: preSelectedTime,
          },
        }
      : {};
    const localStorageState = getOrderPageStateFromLocalStorage(orderPage.id);

    if (localStorageState && ((!orderPageState.voucher && !voucher) || (!!orderPageState.voucher && !!voucher))) {
      setOrderPageState({
        ...orderPageState,
        ...omit(localStorageState, ['account', 'availableDates', 'isLoadingDates', 'orderPage', 'voucher']),
        orderPage,
        account,
        specificEvent,
        ...specificEventDateAndTimeDetails,
        ...specificPreselectedDateAndTimeDetails,
        voucher,
        specificOrderPage: {
          onlyCouples,
          onlyVouchers,
          noMinDate,
          troubleshoot,
          preSelectedDate,
        },
      });
    } else {
      setOrderPageState({
        ...orderPageState,
        specificEvent: specificEvent || undefined,
        account,
        experience,
        orderPage,
        ...specificEventDateAndTimeDetails,
        ...specificPreselectedDateAndTimeDetails,
        details: {
          ...tempDetails,
          price: voucher
            ? voucher.price
            : calculatePrice(
                orderPage,
                tempDetails,
                specificEvent
                  ? specificEventDateAndTimeDetails.dateAndTimeDetails
                  : preSelectedDate
                  ? specificPreselectedDateAndTimeDetails?.dateAndTimeDetails
                  : orderPageState.dateAndTimeDetails,
              ),
        },
        isInitialized: true,
        voucher: voucher || undefined,
        voucherError,
        specificEventError,
        specificOrderPage: {
          onlyCouples,
          onlyVouchers,
          noMinDate,
          troubleshoot,
          preSelectedDate,
        },
      });
    }
  };
  useEffect(() => {
    void (async () => {
      const account = await AccountService.getAccount(accountId);

      let voucher;
      let voucherError;
      let specificEventError;
      let specificEvent = null;
      if (account?.marketing_integrations?.ga4_id) {
        ReactGA.initialize(account?.marketing_integrations?.ga4_id);
      }
      if (voucherCode) {
        posthog.startSessionRecording({});
        posthog.capture('Voucher redemption started', { voucherCode });
        try {
          voucher = await VoucherService.getVoucher(voucherCode);
          if (!includes([VoucherStatus.ACTIVE], voucher.status)) {
            if (voucher.status === VoucherStatus.USED) {
              voucherError = VoucherError.ALREADY_USED;
            }
            if (voucher.status === VoucherStatus.ARCHIVED) {
              voucherError = VoucherError.ARCHIVED;
            }
            voucher = null;
          }
        } catch (e: any) {
          if (e.status === 404) {
            voucherError = VoucherError.NOT_FOUND;
          }
          if (e.status === 400 && e.body.detail === 'voucher is not valid') {
            voucherError = VoucherError.EXPIRED;
          }
        }
      }
      if (specificEventId) {
        specificEvent = await EventService.getSlimEvent(specificEventId);
        if (specificEvent.tickets_left < 1) {
          specificEventError = SpecificEventError.NO_MORE_TICKETS;
        }
      }
      initOrderPageState({ account, voucher, voucherError, specificEvent, specificEventError });
    })();
    setUTMData();
  }, []);
};
